import { useCallback, useEffect, useState } from "react";
import { Moon, Sparkle, Trash, Bed, Siren } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Shared";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Therapy, Breaks } from "@/components/Wellbeing";
import { Stretching } from "@/components/Stretching";
import { useAccess } from "@/context/AccessContext";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const today = () => new Date().toISOString().slice(0, 10);
const BLANK_LOG = { day: today(), sleep_hours: "7", rest_break_minutes: "30",
  travel_to_work_minutes: "45", travel_home_minutes: "45", slept_at: "home",
  devices_away: true, screens_before_bed: false, scroll_minutes: "0", note: "" };
const BLANK_PRODUCT = { name: "", category: "mattress", price: "", currency: "usd", description: "" };

export default function RestSleep() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("mine");
  const [mine, setMine] = useState(null);
  const [pods, setPods] = useState(null);
  const [shop, setShop] = useState(null);
  const [read, setRead] = useState(null);
  const [poster, setPoster] = useState(null);
  const [rules, setRules] = useState(null);
  const [alarm, setAlarm] = useState(null);
  const { announce } = useAccess();
  const [alarmReason, setAlarmReason] = useState("");
  const [campaign, setCampaign] = useState(null);
  const [wind, setWind] = useState({ bedtime: "22:30", winddown_minutes: "60", on: false });
  const [team, setTeam] = useState(null);
  const [log, setLog] = useState(BLANK_LOG);
  const [pod, setPod] = useState({ name: "", site: "", kind: "nap_pod", capacity: "1" });
  const [slot, setSlot] = useState({ day: today(), start: "13:00", end: "13:30" });
  const [product, setProduct] = useState(BLANK_PRODUCT);
  const [busy, setBusy] = useState(false);

  const loadMine = useCallback(() => {
    api.get("/rest-sleep/logs").then(({ data }) => setMine(data)).catch(oops);
  }, []);
  const loadPods = useCallback(() => {
    api.get("/rest-sleep/pods", { params: { day: slot.day } })
      .then(({ data }) => setPods(data)).catch(oops);
  }, [slot.day]);
  const loadShop = useCallback(() => {
    api.get("/rest-sleep/products").then(({ data }) => setShop(data)).catch(oops);
  }, []);

  useEffect(() => { loadMine(); }, [loadMine]);
  useEffect(() => {
    api.get("/rest-sleep/public").then(({ data }) => { setPoster(data.poster); setRules(data.rules); setCampaign(data.campaign); }).catch(() => {});
    api.get("/rest-sleep/winddown").then(({ data }) => {
      if (data.bedtime) setWind({ bedtime: data.bedtime, winddown_minutes: String(data.winddown_minutes), on: data.on });
    }).catch(() => {});
  }, []);

  const saveWind = async (next) => {
    const body = { bedtime: next.bedtime, winddown_minutes: Number(next.winddown_minutes), on: next.on };
    setWind(next);
    try {
      await api.post("/rest-sleep/winddown", body);
      toast.success(next.on ? `Nudge set for ${body.bedtime} and an hour before` : "Nudges off");
    } catch (e) { oops(e); }
  };

  const writeCampaign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rest-sleep/campaign/refresh");
      setCampaign(data); toast.success("Today's message written and posted to the board");
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const loadAlarm = useCallback(() => {
    api.get("/rest-sleep/alarm").then(({ data }) => {
      setAlarm((prev) => {
        // Never rely on a sound: announce it and flash it for anyone who cannot hear.
        if (data?.id && data.id !== prev?.id) {
          announce(`${data.headline}. ${data.instruction}`);
          if (navigator.vibrate) navigator.vibrate([300, 150, 300, 150, 300]);
        }
        return data;
      });
    }).catch(() => {});
  }, [announce]);
  useEffect(() => {
    loadAlarm();
    const t = setInterval(loadAlarm, 30000);
    return () => clearInterval(t);
  }, [loadAlarm]);

  const soundAlarm = async () => {
    try {
      const { data } = await api.post("/rest-sleep/alarm", {
        reason: alarmReason || "Alarm test", test: !alarmReason,
      });
      setAlarm(data); setAlarmReason("");
      toast.success(`${data.woken.length} resting now were woken`);
    } catch (e) { oops(e); }
  };

  const clearAlarm = async () => {
    try { await api.post(`/rest-sleep/alarm/${alarm.id}/clear`); setAlarm(null); } catch (e) { oops(e); }
  };
  useEffect(() => { if (view === "rooms") loadPods(); }, [view, loadPods]);
  useEffect(() => { if (view === "shop" && !shop) loadShop(); }, [view, shop, loadShop]);
  useEffect(() => {
    if (view === "read" && !read) api.get("/rest-sleep/read").then(({ data }) => setRead(data)).catch(oops);
  }, [view, read]);
  useEffect(() => {
    if (view === "team" && !team) api.get("/rest-sleep/team").then(({ data }) => setTeam(data)).catch(oops);
  }, [view, team]);

  const saveLog = async () => {
    try {
      await api.post("/rest-sleep/logs", {
        day: log.day, sleep_hours: Number(log.sleep_hours),
        rest_break_minutes: Number(log.rest_break_minutes),
        travel_to_work_minutes: Number(log.travel_to_work_minutes),
        travel_home_minutes: Number(log.travel_home_minutes),
        scroll_minutes: Number(log.scroll_minutes || 0),
        slept_at: log.slept_at, devices_away: log.devices_away,
        screens_before_bed: log.screens_before_bed, note: log.note || null,
      });
      toast.success("Rest logged"); loadMine();
    } catch (e) { oops(e); }
  };

  const addPod = async () => {
    try {
      await api.post("/rest-sleep/pods", { ...pod, capacity: Number(pod.capacity) });
      setPod({ name: "", site: "", kind: "nap_pod", capacity: "1" });
      toast.success("Rest room added"); loadPods();
    } catch (e) { oops(e); }
  };

  const book = async (podId) => {
    try {
      await api.post("/rest-sleep/bookings", { pod_id: podId, ...slot });
      toast.success("Slot booked"); loadPods();
    } catch (e) { oops(e); }
  };

  const release = async (id) => {
    try { await api.delete(`/rest-sleep/bookings/${id}`); loadPods(); } catch (e) { oops(e); }
  };

  const removePod = async (id) => {
    try { await api.delete(`/rest-sleep/pods/${id}`); loadPods(); } catch (e) { oops(e); }
  };

  const addProduct = async () => {
    setBusy(true);
    try {
      await api.post("/rest-sleep/products", { ...product, price: Number(product.price) });
      setProduct(BLANK_PRODUCT); toast.success("Recovery line added"); loadShop();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const removeProduct = async (id) => {
    try { await api.delete(`/rest-sleep/products/${id}`); loadShop(); } catch (e) { oops(e); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rest-sleep/refresh");
      setRead(data.read);
      if (data.poster?.image_url) setPoster(data.poster.image_url);
      toast.success("Read again by AI");
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const redrawBoard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rest-sleep/poster");
      setPoster(data.image_url); toast.success("Board picture redrawn");
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const TABS = [["mine", "My rest"], ["breaks", "Breaks"], ["stretching", "Stretching"],
    ["therapy", "Therapy"], ["rooms", "Quiet rooms"], ["shop", "Recovery lines"],
    ["read", "The read"], ...(isAdmin ? [["team", "Team roll-up"]] : [])];

  return (
    <div className="space-y-5" data-testid="rest-sleep-page">
      <PageHeader overline="Rest well, work well" title="Rest & Sleep" testid="rest-sleep-header"
        description="Travel time to work, rest breaks on shift and the sleep you got at home — plus the quiet rooms you can book and the recovery lines we sell." />

      <div className="flex flex-wrap items-center gap-2">
        <a href="/rest-sleep-board" target="_blank" rel="noreferrer" data-testid="rest-public-link"
          className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
          Public rest board
        </a>
        <ShareBar url={`${BACKEND}/api/share/rest-sleep`} text="Rest & sleep"
          testid="rest-share" compact instagram />
        {isAdmin && (
          <Button variant="outline" data-testid="rest-refresh" onClick={refresh} disabled={busy}>
            <Sparkle size={15} className="mr-1.5" /> {busy ? "Working…" : "Read it again"}
          </Button>
        )}
        {isAdmin && (
          <Button variant="outline" data-testid="rest-redraw-board" onClick={redrawBoard} disabled={busy}>
            <Sparkle size={15} className="mr-1.5" /> Redraw the board
          </Button>
        )}
      </div>

      {poster && (
        <img data-testid="rest-poster" src={`${BACKEND}${poster}`} alt="Today's rest and sleep board"
          className="h-40 w-full border border-border object-cover sm:h-56" />
      )}

      {alarm && (
        <div data-testid="rest-alarm-banner" role="alert" aria-live="assertive"
          className={`border-l-4 px-4 py-3 ${alarm.test ? "border-[#B77B00] bg-[#FFF8E6]" : "border-[#B3241B] bg-[#FFECEA]"} ${
            alarm.test ? "" : "ring-4 ring-[#B3241B]"}`}>
          <p className="text-sm font-bold uppercase tracking-wide text-[#8E1C15]" data-testid="rest-alarm-headline">
            {alarm.headline}
          </p>
          <p className="mt-1 text-base text-[#0F172A]" data-testid="rest-alarm-instruction">{alarm.instruction}</p>
          <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">
            {(alarm.woken || []).length} resting were woken · by {alarm.engine_label}
          </p>
          {isAdmin && (
            <Button className="mt-2" variant="outline" size="sm" data-testid="rest-alarm-clear" onClick={clearAlarm}>
              Stand it down
            </Button>
          )}
        </div>
      )}

      {rules?.rules?.length > 0 && (
        <div className="border-l-2 border-[#B3241B] bg-[#FFF5F4] p-4" data-testid="rest-rules">
          <p className="overline text-[#B3241B]">No phone, no screen</p>
          <p className="mt-1 text-sm font-semibold text-[#0F172A]">{rules.line}</p>
          <ul className="mt-2 grid gap-1 text-sm text-slate-700 sm:grid-cols-2">
            {rules.rules.map((r, i) => <li key={i}>· {r}</li>)}
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <ShareBar url={`${BACKEND}/api/share/rest-sleep-rule`} text={rules.line || "No phone, no screen"}
              testid="rest-rule-share" compact instagram />
            {isAdmin && (
              <>
                <Input data-testid="rest-alarm-reason" className="w-full sm:w-56"
                  placeholder="Emergency reason (blank = test)"
                  value={alarmReason} onChange={(e) => setAlarmReason(e.target.value)} />
                <Button variant="outline" size="sm" data-testid="rest-alarm-sound" onClick={soundAlarm}>
                  <Siren size={15} className="mr-1.5" /> Sound the alarm
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {campaign?.headline && (
        <div className="border border-border bg-white p-4" data-testid="rest-campaign">
          <p className="overline text-[#B3241B]">Stop the scroll{campaign.day ? ` · ${campaign.day}` : ""}</p>
          <p className="mt-1 text-sm font-semibold text-[#0F172A]">{campaign.headline}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-700">{campaign.message}</p>
          <p className="mt-2 text-xs font-semibold text-[#002FA7]">{(campaign.hashtags || []).join(" ")}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <ShareBar url={`${BACKEND}/api/share/rest-sleep-campaign`} text={campaign.headline}
              testid="rest-campaign-share" compact instagram />
            {isAdmin && (
              <Button variant="outline" size="sm" data-testid="rest-campaign-write" onClick={writeCampaign} disabled={busy}>
                <Sparkle size={15} className="mr-1.5" /> {busy ? "Writing…" : "Write today's message"}
              </Button>
            )}
          </div>
        </div>
      )}

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="scroll-x w-full justify-start overflow-x-auto" data-testid="rest-tabs">
          {TABS.map(([key, label]) => (
            <TabsTrigger key={key} value={key} data-testid={`rest-tab-${key}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {view === "mine" && (
        <div className="space-y-4" data-testid="rest-mine">
          <div className="flex flex-wrap items-end gap-3 border border-border bg-white p-4" data-testid="rest-winddown">
            <div>
              <p className="overline text-slate-500">Stop the scroll</p>
              <p className="text-sm text-slate-600">A nudge an hour before bed, and again at bedtime.</p>
            </div>
            <label className="text-xs font-semibold text-slate-600">Bedtime
              <Input data-testid="rest-winddown-bedtime" type="time" className="w-32" value={wind.bedtime}
                onChange={(e) => setWind({ ...wind, bedtime: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">Wind-down (min)
              <Input data-testid="rest-winddown-minutes" type="number" className="w-24" value={wind.winddown_minutes}
                onChange={(e) => setWind({ ...wind, winddown_minutes: e.target.value })} />
            </label>
            <Button variant={wind.on ? "default" : "outline"} data-testid="rest-winddown-toggle"
              onClick={() => saveWind({ ...wind, on: !wind.on })}>
              {wind.on ? "Nudges on" : "Turn nudges on"}
            </Button>
            {wind.on && (
              <Button variant="outline" data-testid="rest-winddown-save" onClick={() => saveWind(wind)}>Save</Button>
            )}
          </div>

          {mine && (
            <div className="grid gap-3 sm:grid-cols-4">
              {[["Sleep a night", `${mine.summary.average_sleep} h`],
                ["Travel a day", `${mine.summary.average_travel} min`],
                ["Screen-free run", `${mine.summary.screen_free_streak} days`],
                ["Tired days", mine.summary.fatigue_days]].map(([label, value]) => (
                <div key={label} className="border border-border bg-white p-3">
                  <p className="overline text-slate-500">{label}</p>
                  <p className="mt-1 text-xl font-bold text-[#0F172A]">{value}</p>
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-2 border border-border bg-white p-4 sm:grid-cols-3" data-testid="rest-log-form">
            <label className="text-xs font-semibold text-slate-600">Day
              <Input data-testid="rest-log-day" type="date" value={log.day}
                onChange={(e) => setLog({ ...log, day: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">Sleep at home (hours)
              <Input data-testid="rest-log-sleep" type="number" step="0.5" value={log.sleep_hours}
                onChange={(e) => setLog({ ...log, sleep_hours: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">Rest breaks (minutes)
              <Input data-testid="rest-log-breaks" type="number" value={log.rest_break_minutes}
                onChange={(e) => setLog({ ...log, rest_break_minutes: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">Travel to work (minutes)
              <Input data-testid="rest-log-travel-in" type="number" value={log.travel_to_work_minutes}
                onChange={(e) => setLog({ ...log, travel_to_work_minutes: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">Travel home (minutes)
              <Input data-testid="rest-log-travel-out" type="number" value={log.travel_home_minutes}
                onChange={(e) => setLog({ ...log, travel_home_minutes: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">Slept
              <select data-testid="rest-log-where" className="w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={log.slept_at} onChange={(e) => setLog({ ...log, slept_at: e.target.value })}>
                <option value="home">At home</option>
                <option value="pod">In a pod on site</option>
                <option value="away">Away / on the road</option>
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">Scrolling before bed (minutes)
              <Input data-testid="rest-log-scroll" type="number" value={log.scroll_minutes}
                onChange={(e) => setLog({ ...log, scroll_minutes: e.target.value })} />
            </label>
            <div className="sm:col-span-3 flex flex-wrap items-center gap-4">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
                <input data-testid="rest-log-devices" type="checkbox" checked={log.devices_away}
                  onChange={(e) => setLog({ ...log, devices_away: e.target.checked })} />
                Phone and laptop away while resting
              </label>
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
                <input data-testid="rest-log-screens" type="checkbox" checked={log.screens_before_bed}
                  onChange={(e) => setLog({ ...log, screens_before_bed: e.target.checked })} />
                Screens in the hour before bed
              </label>
            </div>
            <div className="sm:col-span-3">
              <Button data-testid="rest-log-save" onClick={saveLog}>Log it</Button>
            </div>
          </div>

          {mine?.logs?.length === 0 ? (
            <EmptyState testid="rest-logs-empty" title="Nothing logged yet"
              description="Log last night's sleep and today's travel to see the pattern build up." />
          ) : (
            <ul className="space-y-2" data-testid="rest-logs">
              {mine?.logs?.map((l, i) => (
                <li key={l.id} data-testid={`rest-log-${i}`}
                  className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
                  <span className="font-semibold text-[#0F172A]">{l.day}</span>
                  <span className="text-slate-600">{l.sleep_hours} h sleep · {l.travel_minutes} min travel · {l.rest_break_minutes} min breaks{l.screen_free ? " · screen-free" : ""}</span>
                  {l.fatigue
                    ? <span data-testid={`rest-log-flag-${i}`} className="border border-[#8E1C15] px-2 py-0.5 text-[11px] font-semibold uppercase text-[#8E1C15]">{l.flags.join(", ")}</span>
                    : <span className="text-[11px] font-semibold uppercase text-[#0B5C2E]">rested</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {view === "breaks" && <Breaks />}
      {view === "stretching" && <Stretching />}
      {view === "therapy" && <Therapy />}

      {view === "rooms" && (
        <div className="space-y-3" data-testid="rest-rooms">
          <div className="flex flex-wrap items-end gap-2 border border-border bg-white p-4">
            <label className="text-xs font-semibold text-slate-600">Day
              <Input data-testid="rest-slot-day" type="date" value={slot.day}
                onChange={(e) => setSlot({ ...slot, day: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">From
              <Input data-testid="rest-slot-start" type="time" value={slot.start}
                onChange={(e) => setSlot({ ...slot, start: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">To
              <Input data-testid="rest-slot-end" type="time" value={slot.end}
                onChange={(e) => setSlot({ ...slot, end: e.target.value })} />
            </label>
          </div>

          {pods?.can_manage && (
            <div className="flex flex-wrap items-end gap-2 border border-border bg-white p-4">
              <Input data-testid="rest-pod-name" className="w-full sm:w-52" placeholder="Quiet room 1"
                value={pod.name} onChange={(e) => setPod({ ...pod, name: e.target.value })} />
              <Input data-testid="rest-pod-site" className="w-full sm:w-40" placeholder="Site"
                value={pod.site} onChange={(e) => setPod({ ...pod, site: e.target.value })} />
              <select data-testid="rest-pod-kind" className="border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={pod.kind} onChange={(e) => setPod({ ...pod, kind: e.target.value })}>
                <option value="nap_pod">Nap pod</option>
                <option value="rest_room">Rest room</option>
                <option value="quiet_room">Quiet room</option>
              </select>
              <Input data-testid="rest-pod-capacity" className="w-20" type="number" value={pod.capacity}
                onChange={(e) => setPod({ ...pod, capacity: e.target.value })} />
              <Button data-testid="rest-pod-add" onClick={addPod} disabled={pod.name.trim().length < 2}>Add room</Button>
            </div>
          )}

          {pods?.pods?.length === 0 ? (
            <EmptyState testid="rest-pods-empty" title="No quiet rooms yet"
              description="An admin can add the nap pods and quiet rooms people can book." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="rest-pods">
              {pods?.pods?.map((p, i) => (
                <div key={p.id} data-testid={`rest-pod-${i}`} className="border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="inline-flex items-center gap-1.5 font-semibold text-[#0F172A]">
                        <Bed size={16} /> {p.name}
                      </p>
                      <p className="text-xs text-slate-500">{LABEL(p.kind)}{p.site ? ` · ${p.site}` : ""} · sleeps {p.capacity} · {p.booked} booked</p>
                    </div>
                    {pods.can_manage && (
                      <button data-testid={`rest-pod-delete-${i}`} onClick={() => removePod(p.id)}
                        className="tap-safe text-xs font-semibold text-slate-500 hover:text-[#B3241B]">
                        <Trash size={13} />
                      </button>
                    )}
                  </div>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {p.slots.map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-2">
                        <span>{s.start}–{s.end} · {s.mine ? "you" : s.user_name}</span>
                        {(s.mine || pods.can_manage) && (
                          <button data-testid={`rest-release-${s.id}`} onClick={() => release(s.id)}
                            className="tap-safe text-xs font-semibold text-slate-500 hover:text-[#B3241B]">Release</button>
                        )}
                      </li>
                    ))}
                    {p.slots.length === 0 && <li className="text-slate-400">Free all day</li>}
                  </ul>
                  <Button className="mt-3" variant="outline" data-testid={`rest-book-${i}`} onClick={() => book(p.id)}>
                    Book {slot.start}–{slot.end}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "shop" && (
        <div className="space-y-3" data-testid="rest-shop">
          {shop?.can_manage && (
            <div className="grid gap-2 border border-border bg-white p-4 sm:grid-cols-4" data-testid="rest-product-form">
              <Input data-testid="rest-product-name" className="sm:col-span-2" placeholder="Memory foam mattress"
                value={product.name} onChange={(e) => setProduct({ ...product, name: e.target.value })} />
              <select data-testid="rest-product-category" className="border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={product.category} onChange={(e) => setProduct({ ...product, category: e.target.value })}>
                {(shop.categories || []).map((c) => <option key={c} value={c}>{LABEL(c)}</option>)}
              </select>
              <Input data-testid="rest-product-price" type="number" placeholder="Price"
                value={product.price} onChange={(e) => setProduct({ ...product, price: e.target.value })} />
              <Input data-testid="rest-product-description" className="sm:col-span-3" placeholder="What it is and who it suits"
                value={product.description} onChange={(e) => setProduct({ ...product, description: e.target.value })} />
              <Button data-testid="rest-product-add" onClick={addProduct}
                disabled={busy || product.name.trim().length < 2 || !product.price}>
                {busy ? "Drawing…" : "Add line"}
              </Button>
            </div>
          )}

          {shop?.products?.length === 0 ? (
            <EmptyState testid="rest-products-empty" title="No recovery lines yet"
              description="Add mattresses, nap pods, eye masks or blackout blinds — they show in the catalogue too." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="rest-products">
              {shop?.products?.map((p, i) => (
                <div key={p.id} data-testid={`rest-product-${i}`} className="border border-border bg-white">
                  {p.image_url && <img src={`${BACKEND}${p.image_url}`} alt={p.name} className="h-36 w-full object-cover" />}
                  <div className="p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">{LABEL(p.category)}</p>
                    <p className="mt-0.5 font-semibold text-[#0F172A]">{p.name}</p>
                    <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{p.symbol}{p.price}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <ShareBar url={`${BACKEND}/api/share/rest-sleep/${p.id}`} text={p.name}
                        testid={`rest-product-share-${i}`} compact instagram />
                      {shop.can_manage && (
                        <button data-testid={`rest-product-delete-${i}`} onClick={() => removeProduct(p.id)}
                          className="tap-safe text-xs font-semibold text-slate-500 hover:text-[#B3241B]">
                          <Trash size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "read" && (
        read ? (
          <div className="space-y-4" data-testid="rest-read">
            <div className="grid gap-3 sm:grid-cols-4">
              {[["Sleep a night", `${read.numbers.average_sleep} h`],
                ["Travel a day", `${read.numbers.average_travel} min`],
                ["Tired days", read.numbers.fatigue_days],
                ["Sleep debt", `${read.numbers.sleep_debt} h`]].map(([label, value]) => (
                <div key={label} className="border border-border bg-white p-3">
                  <p className="overline text-slate-500">{label}</p>
                  <p className="mt-1 text-xl font-bold text-[#0F172A]">{value}</p>
                </div>
              ))}
            </div>
            {read.read && (
              <div className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3" data-testid="rest-read-text">
                <p className="overline text-[#002FA7]">The read · {read.day}</p>
                <p className="mt-1 whitespace-pre-line text-base leading-relaxed text-[#0F172A]">{read.read}</p>
                <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">By {read.engine_label}</p>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {[["Watch", read.watch], ["Do this week", read.do]].map(([title, list]) => (
                <div key={title} className="border border-border bg-white p-4">
                  <p className="text-sm font-semibold text-[#0F172A]">{title}</p>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {(list || []).map((x, n) => <li key={n}>· {x}</li>)}
                    {(list || []).length === 0 && <li className="text-slate-400">Nothing flagged</li>}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ) : <p className="py-8 text-center text-sm text-slate-500">Reading the week…</p>
      )}

      {view === "team" && (
        team ? (
          <div className="space-y-3" data-testid="rest-team">
            {team.people.length === 0 ? (
              <EmptyState testid="rest-team-empty" title="Nobody has logged yet"
                description="Once the team logs their sleep and travel, the roll-up appears here." />
            ) : (
              <ul className="space-y-2">
                {team.people.map((p, i) => (
                  <li key={p.user_id} data-testid={`rest-team-${i}`}
                    className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-[#0F172A]">
                      <Moon size={15} /> {p.name}
                    </span>
                    <span className="text-slate-600">{p.average_sleep} h sleep · {p.average_travel} min travel · {p.screen_free_days}/{p.days} screen-free</span>
                    {p.fatigue_days > 0 && (
                      <span className="border border-[#8E1C15] px-2 py-0.5 text-[11px] font-semibold uppercase text-[#8E1C15]">
                        {p.fatigue_days} tired days
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : <p className="py-8 text-center text-sm text-slate-500">Adding up the team…</p>
      )}
    </div>
  );
}
